import { createPortal } from 'react-dom';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Webhook, Plus, Trash2, RefreshCw,
  Check, Copy, X, Shield, Eye, EyeOff, AlertCircle,
  CheckCircle, Clock, Edit2, Search, ChevronDown
} from 'lucide-react';
import Modal from '../components/Modal';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useAuth } from '../context/AuthContext';
import ContextWarning from '../components/ContextWarning';
import { usePermissions } from '../context/PermissionContext';
import toastService from '../utils/toastService';
import { formatDateTime, localDayToUtcRange } from '../utils/dateTime';

// Named constants 
const DEFAULT_RETRY_INTERVAL_SECONDS = 5;
const ERROR_SCROLL_DELAY_MS = 50;
const COPY_RESET_DELAY_MS = 2000;
const DEBOUNCE_DELAY_MS = 400;
const LABEL_ALL_ASSIGNMENTS = 'All Assignments';
const LABEL_ALL_WEBHOOKS = 'All Webhooks';

const CLASS_TEXT_PRIMARY = 'text-primary';
const CLASS_TEXT_BASE = 'text-base-content';
const CLASS_BG_PRIMARY_MUTED = 'bg-primary/5';

const WEBHOOK_EVENTS = [
  { id: 'assignment.created', desc: 'New assignment created' },
  { id: 'assignment.link_opened', desc: 'Recipient opened capture link' },
  { id: 'assignment.capture_started', desc: 'Capture process started' },
  { id: 'assignment.capture_completed', desc: 'Step completed' },
  { id: 'assignment.completed', desc: 'All steps completed' },
  { id: 'assignment.expired', desc: 'Link expired' },
];

// ─── Register / Edit Modal ────────────────────────────────────────────────────
const WebhookFormModal = ({ webhook, onClose, onSaved }) => {
  const isEdit = !!webhook;
  const [url, setUrl] = useState(webhook?.url || '');
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [events, setEvents] = useState(() => {
    if (!webhook?.events) { return []; }
    return webhook.events.filter(e => WEBHOOK_EVENTS.some(we => we.id === e));
  });
  const [active, setActive] = useState(webhook?.active ?? true);
  const [pendingActive, setPendingActive] = useState(null);
  const [showActiveConfirm, setShowActiveConfirm] = useState(false);
  const [retryCount, setRetryCount] = useState(webhook?.maxRetryAttempts ?? 1);
  const [retryInterval, setRetryInterval] = useState(webhook?.retryIntervalSeconds ?? DEFAULT_RETRY_INTERVAL_SECONDS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isChangingSecret, setIsChangingSecret] = useState(false);
  const [showSecretConfirm, setShowSecretConfirm] = useState(false);
  const errorRef = useRef(null);

  const handleActiveChange = (checked) => {
    if (!isEdit) {
      setActive(checked);
      return;
    }

    if (checked === active) { return; }

    setPendingActive(checked);
    setShowActiveConfirm(true);
  };

  const handleActiveConfirm = () => {
    setActive(pendingActive);
    setPendingActive(null);
    setShowActiveConfirm(false);
  };

  const handleActiveCancel = () => {
    setPendingActive(null);
    setShowActiveConfirm(false);
  };

  const toggleEvent = (id) =>
    setEvents((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));

  const handleSubmit = async () => {
    const showErrorMsg = (msg) => {
      setError(msg);
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, ERROR_SCROLL_DELAY_MS);
    };

    const trimmedUrl = url.trim();
    if (!trimmedUrl) { showErrorMsg('Webhook URL is required'); return; }

    // URL Format Validation
    try {
      const parsedUrl = new URL(trimmedUrl);
      if (parsedUrl.protocol !== 'https:') {
        showErrorMsg('Only HTTPS URLs are allowed');
        return;
      }
    } catch (e) {
      showErrorMsg('Please enter a valid URL (e.g., https://example.com/webhook)');
      return;
    }

    if (events.length === 0) { showErrorMsg('Select at least one event'); return; }

    // Signing Secret validation
    if (!isEdit && !secret.trim()) {
      showErrorMsg('Signing Secret is required');
      return;
    }

    if (isEdit && isChangingSecret && !secret.trim()) {
      showErrorMsg('Please enter a new secret or cancel the update');
      return;
    }

    setError('');
    setSaving(true);
    try {
      const body = {
        url: url.trim(),
        events,
        active,
        maxRetryAttempts: Number(retryCount),
        retryIntervalSeconds: Number(retryInterval)
      };

      // Always send secret, use null if empty to clear it in the backend
      body.secret = secret ? secret : null;

      if (isEdit) {
        await apiClient.patch(`/webhooks/${webhook.id}`, body);
        toastService.success('Webhook updated successfully');
      } else {
        await apiClient.post('/webhooks', body);
        toastService.success('Webhook registered successfully');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message;
      showErrorMsg(Array.isArray(msg) ? msg.join(', ') : (msg || 'Failed to save webhook'));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-base-100 w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-in-up">

        {/* Header */}
        <div className="px-6 py-5 border-b border-base-content/10 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Webhook size={18} className="text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-black text-base-content tracking-tight">
                {isEdit ? 'Update Webhook' : 'Register Webhook'}
              </h3>
            </div>
          </div>
          <button onClick={onClose} className="text-base-content/40 hover:text-base-content transition-colors p-1">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {error && (
            <div ref={errorRef} className="mt-4 p-3 bg-error/10 border-2 border-error/20 rounded-xl flex items-start gap-3">
              <AlertCircle size={16} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-error font-bold text-xs leading-relaxed">{error}</p>
            </div>
          )}

          {/* Status Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-base-200/50 border border-base-content/10">
            <div>
              <p className="text-xs font-black text-base-content tracking-tight">Active Status</p>
            </div>
            <label aria-label="Active Status toggle" className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" checked={active} onChange={(e) => handleActiveChange(e.target.checked)} />
              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary shadow-inner"></div>
            </label>
          </div>
          <Modal
            isOpen={showActiveConfirm}
            onClose={handleActiveCancel}
            title={`${pendingActive ? 'Activate' : 'Deactivate'} Webhook`}
          >
            <div className="mb-6">
              <p className="text-sm text-slate-600">
                Are you sure you want to {pendingActive ? 'activate' : 'deactivate'} this webhook? {pendingActive ? 'Active webhooks continue delivering events.' : 'Disabling it will stop event delivery until it is reactivated.'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleActiveCancel}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleActiveConfirm}
                className="flex-1 px-4 py-2 bg-primary text-primary-content rounded-xl font-medium hover:bg-primary/90 transition"
              >
                {pendingActive ? 'Activate' : 'Deactivate'}
              </button>
            </div>
          </Modal>

          {/* URL */}
          <div className="space-y-2">
            <label htmlFor="webhookUrl" className="text-[10px] font-black text-base-content/40 uppercase tracking-widest ml-1">
              Webhook URL <span className="text-error">*</span>
            </label>
            <input
              id="webhookUrl"
              type="url"
              value={url}
              name='webhook url'
              autoComplete="off"
              onChange={(e) => setUrl(e.target.value)}
              className="w-full rounded-xl px-4 py-3 bg-base-200/50 border border-base-content/10 text-sm  focus:border-primary focus:bg-base-100 outline-none transition-all"
            />
          </div>

          {/* Secret */}
          <div className="space-y-2">
            <label htmlFor="webhookSecret" className="text-[10px] font-black text-base-content/40 uppercase tracking-widest ml-1">
              Webhook Secret <span className="text-error">*</span>
            </label>

            {isEdit && !isChangingSecret ? (
              <div className="flex items-center justify-between px-4 py-2 rounded-xl bg-base-200/50 border border-base-content/10">
                <div className="flex items-center gap-2">
                  <Shield size={14} className="text-base-content/20" />
                  <span className="font-mono text-sm tracking-widest text-base-content/30 ">••••••••••••••••••••••••••••••</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSecretConfirm(true)}
                  className="px-4 py-2 rounded-xl bg-primary/10 text-primary text-xs font-black hover:bg-primary hover:text-primary-content transition-all"
                >
                  Reset Secret
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  id="webhookSecret"
                  type={showSecret ? 'text' : 'password'}
                  value={secret}
                  autoComplete="new-password"
                  name="secret"
                  onChange={(e) => setSecret(e.target.value)}
                  maxLength={128}
                  placeholder={isEdit ? "Enter new secret..." : "Enter signing secret..."}
                  className="w-full rounded-xl px-4 py-3 pr-12 bg-base-200/50 border border-base-content/10 text-sm focus:border-primary focus:bg-base-100 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-primary transition-colors"
                >
                  {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                {isEdit && isChangingSecret && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsChangingSecret(false);
                      setSecret('');
                    }}
                    className="absolute -top-7 right-0 text-[10px] font-black text-primary uppercase tracking-wider hover:underline"
                  >
                    Cancel Update
                  </button>
                )}
              </div>
            )}

            <p className="text-[11px] text-base-content/80 tracking-wide ml-1">
              Used to sign payloads via HMAC-SHA256 (X-Send2Me-Signature in header)
            </p>
          </div>

          <Modal
            isOpen={showSecretConfirm}
            onClose={() => setShowSecretConfirm(false)}
            title="Reset Webhook Secret"
          >
            <div className="mb-6">
              <p className="text-sm text-base-content/60 leading-relaxed">
                This will invalidate the current secret. Existing integrations using the old secret will stop working.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSecretConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setIsChangingSecret(true);
                  setShowSecretConfirm(false);
                }}
                className="flex-1 px-4 py-2.5 bg-primary text-primary-content rounded-xl font-bold text-sm hover:opacity-90 transition"
              >
                Reset
              </button>
            </div>
          </Modal>

          {/* Retry Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="retryCount" className="text-[14px] text-base-content/40  ml-1">
                Retry Count
              </label>
              <select
                id="retryCount"
                value={retryCount}
                onChange={(e) => setRetryCount(Number(e.target.value))}
                className="w-full rounded-xl px-4 py-3 bg-base-200/50 border border-base-content/10 text-sm focus:border-primary focus:bg-base-100 outline-none transition-all cursor-pointer"
              >
                <option value={1}>1 Retry</option>
                <option value={2}>2 Retries</option>
                <option value={3}>3 Retries</option>
              </select>
            </div>
            <div className="space-y-2">
              <label htmlFor="retryDelay" className="text-[14px] text-base-content/40  ml-1">
                Retry Delay (Secs)
              </label>
              <input
                id="retryDelay"
                type="number"
                min="1"
                max="300"
                value={retryInterval}
                onChange={(e) => setRetryInterval(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full rounded-xl px-4 py-3 bg-base-200/50 border border-base-content/10 text-sm focus:border-primary focus:bg-base-100 outline-none transition-all"
              />
            </div>
          </div>

          {/* Events */}
          <div className="space-y-3">
            <div className="flex items-center justify-between ml-1">
              <span className="text-[14px] font-black text-base-content/40 ">
                Subscribed Events <span className="text-error">*</span>
              </span>
              <button
                type="button"
                onClick={() => setEvents(events.length === WEBHOOK_EVENTS.length ? [] : WEBHOOK_EVENTS.map(e => e.id))}
                className="text-[14px] font-black text-primary  hover:underline"
              >
                {events.length === WEBHOOK_EVENTS.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {WEBHOOK_EVENTS.map((event) => (
                <div
                  key={event.id}
                  onClick={() => toggleEvent(event.id)}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${events.includes(event.id)
                    ? 'bg-primary/5 border-primary'
                    : 'bg-base-200/50 border-transparent hover:border-base-content/10'
                    }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center border-2 flex-shrink-0 transition-all ${events.includes(event.id) ? 'bg-primary border-primary text-primary-content' : 'bg-base-100 border-base-content/10'
                    }`}>
                    {events.includes(event.id) && <Check size={12} strokeWidth={4} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[14px] font-bold text-base-content truncate">{event.id}</p>
                    <p className="text-[12px] font-semibol text-base-content/80  mt-0.5">{event.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-base-content/10 flex items-end justify-end gap-4 flex-shrink-0">

          {isEdit && <div />}
          <div className="flex gap-3">
            <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-base-200 text-base-content/60 text-[14px] font-black  hover:bg-base-300 transition-all">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-primary-content text-[14px] font-black hover:opacity-90 transition-all disabled:opacity-50"
            >
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              {saving ? (isEdit ? 'Updating...' : 'Registering...') : (isEdit ? 'Update Webhook' : 'Register Webhook')}
            </button>
          </div>
        </div>
      </div>
    </div>
    , document.body);
};

WebhookFormModal.propTypes = {
  webhook: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    url: PropTypes.string,
    events: PropTypes.arrayOf(PropTypes.string),
    active: PropTypes.bool,
    maxRetryAttempts: PropTypes.number,
    retryIntervalSeconds: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
const DeleteModal = ({ webhook, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/webhooks/${webhook.id}`);
      toastService.success('Webhook deleted successfully');
      onDeleted();
    } catch (err) {
      toastService.error(err.response?.data?.message || 'Failed to delete webhook');
      setDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Delete Webhook"
    >
      <div className="mb-6">
        <p className="text-base-content/60 text-sm">
          Are you sure you want to delete the webhook endpoint{' '}
          <strong className="text-base-content custom-line-break">{webhook.url}</strong>? This action cannot be undone. Past delivery history will be preserved.
        </p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
          disabled={deleting}
        >
          Cancel
        </button>
        <button
          onClick={handleDelete}
          className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete Webhook'}
        </button>
      </div>
    </Modal>
  );
};

DeleteModal.propTypes = {
  webhook: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
};

// ─── Delivery Inspect Modal ───────────────────────────────────────────────────
const DeliveryModal = ({ delivery, onClose }) => {
  const [tab, setTab] = useState('Request');
  const [copied, setCopied] = useState(false);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), COPY_RESET_DELAY_MS);
  };

  const formatJson = (val) => {
    try { return JSON.stringify(typeof val === 'string' ? JSON.parse(val) : val, null, 2); }
    catch { return String(val); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-base-100 w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-in-up">
        <div className="px-6 py-4 border-b border-base-content/10 flex flex-col sm:flex-row sm:items-center justify-between flex-shrink-0 gap-3">
          <div>
            <h3 className="text-sm font-semibold text-base-content tracking-tight">Delivery Details</h3>
            {delivery.submitterName && (
              <p className="text-[14px] text-base-content/40  mt-0.5">
                Assignment : <span className="text-primary">{delivery.submitterName}</span> {delivery.systemReferenceNumber ? `(${delivery.systemReferenceNumber})` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-base-content/40 hover:text-base-content transition-colors self-end sm:self-auto"><X size={20} /></button>
        </div>
        <div className="px-6 border-b border-base-content/10 flex gap-6 flex-shrink-0">
          {/* Timeline remove if want to add then in add below array ['Request', 'Response', 'Timeline']*/}
          {['Request', 'Response'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`py-3 text-[14px] font-black  transition-all border-b-2 ${tab === t ? 'border-primary text-primary' : 'border-transparent text-base-content/40 hover:text-base-content'}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {tab === 'Request' && (
            <>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-black text-base-content/30 uppercase tracking-widest">Method</span>
                <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-black">POST</span>
              </div>
              <div className="space-y-1">
                <span className="text-[14px] font-black text-base-content/30 ">URL</span>
                <div className="bg-base-200/50 rounded-xl p-3 font-mono text-xs text-base-content/70 custom-line-break">{delivery.requestUrl}</div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[14px] font-black text-base-content/30 ">Headers</span>
                  <button onClick={() => copy(formatJson(delivery.requestHeaders))} className="flex items-center gap-1 text-[14px] font-black text-primary hover:underline">
                    {copied ? <Check size={12} /> : <Copy size={12} />} Copy
                  </button>
                </div>
                <div className="bg-[#0d1117] rounded-xl p-4 overflow-x-auto">
                  <pre className="text-xs font-mono text-emerald-400 leading-relaxed">{formatJson(delivery.requestHeaders)}</pre>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[14px] font-black text-base-content/30 ">Payload</span>
                  <button onClick={() => copy(formatJson(delivery.requestPayload))} className="flex items-center gap-1 text-[14px] font-black text-primary hover:underline">
                    {copied ? <Check size={12} /> : <Copy size={12} />} Copy
                  </button>
                </div>
                <div className="bg-[#0d1117] rounded-xl p-4 overflow-x-auto">
                  <pre className="text-xs font-mono text-emerald-400 leading-relaxed">{formatJson(delivery.requestPayload)}</pre>
                </div>
              </div>
            </>
          )}
          {tab === 'Response' && (
            <>
              <div className="flex items-center gap-3">
                <span className="text-[14px] font-black text-base-content/30 ">Status</span>
                <span className={`px-2 py-0.5 rounded-md text-xs font-black ${delivery.responseStatus >= 200 && delivery.responseStatus < 300 ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>
                  {delivery.responseStatus ?? 'N/A'}
                </span>
                <span className="text-[14px] font-black text-base-content/30 ">{delivery.responseTimeMs}ms</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[14px] font-black text-base-content/30 ">Body</span>
                  {delivery.responseBody && (
                    <button onClick={() => copy(formatJson(delivery.responseBody))} className="flex items-center gap-1 text-[14px] font-black text-primary hover:underline">
                      {copied ? <Check size={12} /> : <Copy size={12} />} Copy
                    </button>
                  )}
                </div>
                <div className="bg-[#0d1117] rounded-xl p-4 overflow-x-auto">
                  <pre className="text-xs font-mono text-emerald-400 leading-relaxed custom-line-break whitespace-pre-wrap">
                    {delivery.responseBody ? formatJson(delivery.responseBody) : 'No body'}
                  </pre>
                </div>
              </div>
            </>
          )}
          {/* For now it is hide to enable it remove the false from below line */}
          {tab === 'Timeline' && false && (
            <div className="space-y-0 relative before:absolute before:inset-0 before:ml-[1.15rem] before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-base-content/10 before:to-transparent pt-4 pb-8">
              {/* Step 1: Triggered */}
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active mb-6">
                <div className="flex items-center justify-center w-6 h-6 rounded-full border-4 border-base-100 bg-primary text-primary-content shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                  <div className="w-2 h-2 rounded-full bg-base-100"></div>
                </div>
                <div className="w-[calc(100%-3rem)] md:w-[calc(50%-1.5rem)] bg-base-200/50 p-4 rounded-xl shadow-sm border border-base-content/5">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-sm text-base-content">Event Triggered</h4>
                      <span className="text-[14px] font-black text-base-content/40  font-mono">
                        {new Date(delivery.firedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                      </span>
                    </div>
                    <p className="text-xs text-base-content/60 font-medium">Payload generated for <span className="text-primary font-mono">{delivery.eventType}</span></p>
                  </div>
                </div>
              </div>

              {/* Step 2: Request Sent */}
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active mb-6">
                <div className="flex items-center justify-center w-6 h-6 rounded-full border-4 border-base-100 bg-primary text-primary-content shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                  <div className="w-2 h-2 rounded-full bg-base-100"></div>
                </div>
                <div className="w-[calc(100%-3rem)] md:w-[calc(50%-1.5rem)] bg-base-200/50 p-4 rounded-xl shadow-sm border border-base-content/5">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-sm text-base-content">Request Sent</h4>
                      <span className="text-[14px] font-black text-base-content/40  font-mono">
                        + 0ms
                      </span>
                    </div>
                    <p className="text-xs text-base-content/60 font-medium">POST request dispatched to target URL</p>
                  </div>
                </div>
              </div>

              {/* Step 3: Response Received */}
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                <div className={`flex items-center justify-center w-6 h-6 rounded-full border-4 border-base-100 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 ${delivery.status === 'success' ? 'bg-primary text-primary-content' : 'bg-error text-error-content'}`}>
                  <div className="w-2 h-2 rounded-full bg-base-100"></div>
                </div>
                <div className="w-[calc(100%-3rem)] md:w-[calc(50%-1.5rem)] bg-base-200/50 p-4 rounded-xl shadow-sm border border-base-content/5">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-sm text-base-content">Response Received</h4>
                      <span className="text-[14px] font-black text-base-content/40  font-mono">
                        + {delivery.responseTimeMs}ms
                      </span>
                    </div>
                    <p className="text-xs text-base-content/60 font-medium">
                      Target responded with Status <span className={`font-black  text-[14px] px-1.5 py-0.5 rounded ${delivery.status === 'success' ? 'bg-primary/10 text-primary' : 'bg-error/10 text-error'}`}>{delivery.responseStatus || (delivery.status === 'success' ? '200' : 'Error')}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    , document.body);
};

DeliveryModal.propTypes = {
  delivery: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    systemReferenceNumber: PropTypes.string,
    requestUrl: PropTypes.string,
    requestHeaders: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    requestPayload: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    responseStatus: PropTypes.number,
    responseTimeMs: PropTypes.number,
    responseBody: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    firedAt: PropTypes.string,
    eventType: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

// ─── Main Webhooks Page ───────────────────────────────────────────────────────
const Webhooks = () => {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editWebhook, setEditWebhook] = useState(null);
  const [deleteWebhook, setDeleteWebhook] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    return can.webhooks.view() ? 'registered' : '';
  });

  // Deliveries
  const [deliveries, setDeliveries] = useState([]);
  const [totalDeliveries, setTotalDeliveries] = useState(0);
  const [deliveriesPage, setDeliveriesPage] = useState(1);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [filterDate, setFilterDate] = useState('');
  const [filterAssignment, setFilterAssignment] = useState('');
  const [filterWebhook, setFilterWebhook] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [debouncedFilterAssignment, setDebouncedFilterAssignment] = useState('');
  const [assignmentsList, setAssignmentsList] = useState([]);
  const [asgnSearch, setAsgnSearch] = useState('');
  const [isAsgnDropdownOpen, setIsAsgnDropdownOpen] = useState(false);
  const [isWebhookDropdownOpen, setIsWebhookDropdownOpen] = useState(false);
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const suggestionRef = useRef(null);
  const webhookRef = useRef(null);
  const eventRef = useRef(null);
  const statusRef = useRef(null);
  const LIMIT = 10;

  const fetchWebhooks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/webhooks');
      setWebhooks(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce assignment filter
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterAssignment(filterAssignment);
    }, DEBOUNCE_DELAY_MS); // 400ms debounce
    return () => clearTimeout(handler);
  }, [filterAssignment]);

  const fetchDeliveries = useCallback(async () => {
    setLoadingDeliveries(true);
    try {
      const queryParams = new URLSearchParams({ page: deliveriesPage.toString(), limit: LIMIT.toString() });
      if (filterDate) {
        const { from, to } = localDayToUtcRange(filterDate);
        queryParams.append('dateFrom', from);
        queryParams.append('dateTo', to);
      }
      if (debouncedFilterAssignment) { queryParams.append('assignment', debouncedFilterAssignment); }
      if (filterWebhook) { queryParams.append('webhook', filterWebhook); }
      if (filterEvent) { queryParams.append('eventType', filterEvent); }
      if (filterStatus) { queryParams.append('status', filterStatus); }

      const res = await apiClient.get(`/webhook-deliveries?${queryParams.toString()}`);
      setDeliveries(res.data.data || []);
      setTotalDeliveries(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDeliveries(false);
    }
  }, [deliveriesPage, filterDate, debouncedFilterAssignment, filterWebhook, filterEvent, filterStatus]);

  const fetchAssignmentsList = useCallback(async () => {
    try {
      const res = await apiClient.get('/assignments', { params: { limit: 1000 } });
      setAssignmentsList(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);
  useEffect(() => {
    if (activeTab === 'deliveries') {
      fetchDeliveries();
      if (assignmentsList.length === 0) { fetchAssignmentsList(); }
    }
  }, [activeTab, fetchDeliveries, fetchAssignmentsList, assignmentsList.length]);

  // Handle outside click for suggestions and assignment dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
        setIsAsgnDropdownOpen(false);
      }
      if (webhookRef.current && !webhookRef.current.contains(event.target)) {
        setIsWebhookDropdownOpen(false);
      }
      if (eventRef.current && !eventRef.current.contains(event.target)) {
        setIsEventDropdownOpen(false);
      }
      if (statusRef.current && !statusRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredAssignments = assignmentsList.filter(a =>
    a.submitterName.toLowerCase().includes(asgnSearch.toLowerCase()) ||
    (a.systemReferenceNumber && a.systemReferenceNumber.toLowerCase().includes(asgnSearch.toLowerCase()))
  ).sort((a, b) => a.submitterName.localeCompare(b.submitterName));

  const handleSaved = () => {
    setShowForm(false);
    setEditWebhook(null);
    fetchWebhooks();
  };

  const handleDeleted = () => {
    setDeleteWebhook(null);
    fetchWebhooks();
  };

  const totalPages = Math.ceil(totalDeliveries / LIMIT);

  return (
    <div className="pb-20 animate-fade-in text-base-content">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="page-title">Webhooks</h1>
          <p className="page-subtitle">Real-time event delivery to your endpoints</p>
        </div>
        {can.webhooks.create() && user?.subAccountUID && (
          <button
            onClick={() => setShowForm(true)}
            className="btn-premium w-full sm:w-auto"
          >
            <Plus size={18} />
            Register Webhook
          </button>
        )}
      </div>

      <ContextWarning title="Select a sub-account to create or edit webhooks" message="You're viewing all accounts. To create, edit, or delete a webhook, select a sub-account from the switcher in the bottom left" />

      {/* Tabs */}
      {can.webhooks.view() && (
        <div className="inline-flex gap-1 p-1 bg-base-content/5 rounded-xl mb-6">
          {[
            { id: 'registered', label: 'Registered' },
            { id: 'deliveries', label: 'Delivery History' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-[14px] font-black  transition-all ${activeTab === tab.id
                ? 'bg-base-100 text-primary shadow-lg shadow-black/5'
                : 'text-base-content/40 hover:text-base-content'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Registered Webhooks Tab ────────────────────────────────────────── */}
      {activeTab === 'registered' && (
        <div className="space-y-4 animate-fade-in">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 space-y-4">
              <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              <p className="text-base-content/40 font-black uppercase  text-xs animate-pulse">Loading webhooks...</p>
            </div>
          ) : webhooks.length === 0 ? (
            <div className="card-premium rounded-2xl glass-panel border border-base-content/10 p-16 flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Webhook size={32} className="text-primary/50" />
              </div>
              <div>
                <p className="text-2xl font-black text-base-content tracking-tight mb-2">No webhooks registered</p>
                <p className="text-base-content/50 mb-8 max-w-sm mx-auto font-bold text-[12px]">
                  Register your first endpoint to start receiving events
                </p>
              </div>
            </div>
          ) : (
            webhooks.map((wh) => (
              <div
                key={wh.id}
                className="card-premium rounded-2xl glass-panel border border-base-content/10 p-5 hover:border-primary/20 transition-all"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0">
                    <div className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${wh.active ? 'bg-success/10 border-success/20' : 'bg-base-content/5 border-base-content/10'}`}>
                      <Webhook size={18} className={wh.active ? 'text-success' : 'text-base-content/30'} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-sm font-black text-base-content custom-line-break">{wh.url}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black  ${wh.active ? 'bg-success/10 text-success' : 'bg-base-content/5 text-base-content/30'}`}>
                          {wh.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(wh.events || []).map((ev) => (
                          <span key={ev} className="px-2 py-0.5 bg-base-200 rounded-md text-[10px] font-black text-base-content/50 ">
                            {ev}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex items-center gap-1 text-[14px] font-black text-base-content/25 ">
                      <Clock size={11} />
                      {formatDateTime(wh.createdAt)}
                    </div>
                    {can.webhooks.update() && <button
                      onClick={() => setEditWebhook(wh)}
                      className="px-2 py-2 rounded-xl bg-primary/10 text-primary text-xs font-black hover:bg-primary hover:text-primary-content transition-all"
                    >
                      <Edit2 size={16} />
                    </button>}
                    {can.webhooks.delete() && <button
                      onClick={() => setDeleteWebhook(wh)}
                      className="p-2 rounded-xl bg-error/10 text-error hover:bg-error hover:text-white transition-all"
                    >
                      <Trash2 size={14} />
                    </button>}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Delivery History Tab ───────────────────────────────────────────── */}
      {activeTab === 'deliveries' && (
        <div className="card-premium rounded-2xl glass-panel border border-base-content/10 p-5 animate-fade-in">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-5 gap-3">
            <div>
              <h2 className="text-[16] font-bold text-base-content tracking-tight">Delivery History</h2>
              <p className="text-[12px] font-semibold text-base-content/40 tracking-wide mt-0.5">
                {totalDeliveries} Records
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3 sm:mt-0">
              <input
                type="date"
                value={filterDate}
                onChange={(e) => { setDeliveriesPage(1); setFilterDate(e.target.value); }}
                onClick={(e) => e.target.showPicker?.()}
                className="rounded-xl px-3 py-2 bg-base-200/50 border border-base-content/10 text-xs font-semibold focus:border-primary outline-none cursor-pointer"
              />
              <div className="relative" ref={suggestionRef}>
                <button
                  onClick={() => setIsAsgnDropdownOpen(!isAsgnDropdownOpen)}
                  className="flex items-center justify-between gap-2 rounded-xl px-3 py-2 bg-base-200/50 border border-base-content/10 text-xs font-semibold focus:border-primary outline-none w-48 appearance-none cursor-pointer group"
                >
                  <span className="truncate flex-1 text-left">
                    {filterAssignment || LABEL_ALL_ASSIGNMENTS}
                  </span>
                  <ChevronDown size={14} className={`text-base-content/30 group-hover:text-primary transition-transform ${isAsgnDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isAsgnDropdownOpen && (
                  <div className="absolute left-0 top-full mt-2 min-w-full w-max max-w-sm z-[110] animate-fade-in">
                    <div className="absolute -top-1.5 left-6 w-3 h-3 bg-base-100 border-l border-t border-base-content/10 rotate-45 z-0" />
                    <div className="relative bg-base-100 border border-base-content/10 rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-72 z-10">
                      <div className="p-2 border-b border-base-content/5 bg-base-200/30">
                        <div className="relative">
                          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/20" />
                          <input
                            type="text"
                            placeholder="Filter assignments..."
                            value={asgnSearch}
                            onChange={(e) => setAsgnSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="w-full bg-base-100 border border-base-content/5 rounded-lg py-1.5 pl-8 pr-3 text-[11px] font-bold focus:outline-none focus:border-primary/50"
                            autoFocus
                          />
                        </div>
                      </div>

                      <div className="overflow-y-auto no-scrollbar flex-1">
                        <button
                          onClick={() => {
                            setFilterAssignment('');
                            setIsAsgnDropdownOpen(false);
                            setAsgnSearch('');
                            setDeliveriesPage(1);
                          }}
                          className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 flex items-center gap-2 group ${!filterAssignment ? CLASS_BG_PRIMARY_MUTED : ''}`}
                        >
                          <span className={`text-[11px] font-black uppercase tracking-widest ${!filterAssignment ? CLASS_TEXT_PRIMARY : 'text-base-content/30 group-hover:text-primary'}`}>{LABEL_ALL_ASSIGNMENTS}</span>
                          {!filterAssignment && <Check size={12} className={CLASS_TEXT_PRIMARY} />}
                        </button>

                        {filteredAssignments.length === 0 ? (
                          <div className="px-4 py-8 text-center">
                            <p className="text-[10px] font-black text-base-content/20 uppercase tracking-widest">
                              No matching results
                            </p>
                          </div>
                        ) : (
                          filteredAssignments.sort((a, b) =>
                            a.submitterName.localeCompare(
                              b.submitterName,
                              undefined,
                              { sensitivity: 'base' }
                            )).map((a) => (
                              <button
                                key={a.id}
                                onClick={() => {
                                  setFilterAssignment(a.submitterName);
                                  setIsAsgnDropdownOpen(false);
                                  setAsgnSearch('');
                                  setDeliveriesPage(1);
                                }}
                                className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 last:border-none group flex items-start justify-between gap-2 ${filterAssignment === a.submitterName ? CLASS_BG_PRIMARY_MUTED : ''
                                  }`}
                              >
                                <div className="flex flex-col gap-0.5 min-w-0">
                                  <span
                                    className={`text-xs font-black truncate ${filterAssignment === a.submitterName
                                      ? CLASS_TEXT_PRIMARY
                                      : CLASS_TEXT_BASE
                                      }`}
                                  >
                                    {a.submitterName}
                                  </span>

                                  {a.systemReferenceNumber && (
                                    <span className="text-[9px] font-bold text-base-content/30 uppercase tracking-widest truncate">
                                      {a.systemReferenceNumber}
                                    </span>
                                  )}
                                </div>

                                {filterAssignment === a.submitterName && (
                                  <Check size={12} className="text-primary mt-1 shrink-0" />
                                )}
                                {filterAssignment === a.submitterName && <Check size={12} className="text-primary mt-1 shrink-0" />}
                              </button>
                            )
                            )
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={webhookRef}>
                <button
                  onClick={() => setIsWebhookDropdownOpen(!isWebhookDropdownOpen)}
                  className="flex items-center justify-between gap-2 rounded-xl px-3 py-2 bg-base-200/50 border border-base-content/10 text-xs font-semibold focus:border-primary outline-none w-40 appearance-none cursor-pointer group"
                >
                  <span className="truncate flex-1 text-left">
                    {filterWebhook || LABEL_ALL_WEBHOOKS}
                  </span>
                  <ChevronDown size={14} className={`text-base-content/30 group-hover:text-primary transition-transform ${isWebhookDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isWebhookDropdownOpen && (
                  <div className="absolute left-0 top-full mt-2 min-w-full w-max max-w-sm z-[110] animate-fade-in">
                    <div className="absolute -top-1.5 left-6 w-3 h-3 bg-base-100 border-l border-t border-base-content/10 rotate-45 z-0" />
                    <div className="relative bg-base-100 border border-base-content/10 rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-72 z-10">
                      <div className="overflow-y-auto no-scrollbar flex-1">
                        <button
                          onClick={() => {
                            setFilterWebhook('');
                            setIsWebhookDropdownOpen(false);
                            setDeliveriesPage(1);
                          }}
                          className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 flex items-center gap-2 group ${!filterWebhook ? 'bg-primary/5' : ''}`}
                        >
                          <span className={`text-[11px] font-black uppercase tracking-widest ${!filterWebhook ? 'text-primary' : 'text-base-content/30 group-hover:text-primary'}`}>{LABEL_ALL_WEBHOOKS}</span>
                          {!filterWebhook && <Check size={12} className="text-primary shrink-0" />}
                        </button>

                        {[...new Set(webhooks.map(w => w.url))]
                          .sort((a, b) =>
                            a.localeCompare(b, undefined, { sensitivity: 'base' })
                          )
                          .map((url) => (
                            <button
                              key={url}
                              onClick={() => {
                                setFilterWebhook(url);
                                setIsWebhookDropdownOpen(false);
                                setDeliveriesPage(1);
                              }}
                              className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 last:border-none group flex items-start justify-between gap-2 ${filterWebhook === url ? 'bg-primary/5' : ''
                                }`}
                            >
                              <span
                                className={`text-xs font-black truncate ${filterWebhook === url ? 'text-primary' : 'text-base-content'
                                  }`}
                              >
                                {url}
                              </span>

                              {filterWebhook === url && (
                                <Check size={12} className="text-primary mt-0.5 shrink-0" />
                              )}
                            </button>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={eventRef}>
                <button
                  onClick={() => setIsEventDropdownOpen(!isEventDropdownOpen)}
                  className="flex items-center justify-between gap-2 rounded-xl px-3 py-2 bg-base-200/50 border border-base-content/10 text-xs font-semibold focus:border-primary outline-none w-40 appearance-none cursor-pointer group"
                >
                  <span className="truncate flex-1 text-left">
                    {filterEvent ? WEBHOOK_EVENTS.find(e => e.id === filterEvent)?.id : 'All Events'}
                  </span>
                  <ChevronDown size={14} className={`text-base-content/30 group-hover:text-primary transition-transform ${isEventDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isEventDropdownOpen && (
                  <div className="absolute left-0 top-full mt-2 min-w-full w-max max-w-sm z-[110] animate-fade-in">
                    <div className="absolute -top-1.5 left-6 w-3 h-3 bg-base-100 border-l border-t border-base-content/10 rotate-45 z-0" />
                    <div className="relative bg-base-100 border border-base-content/10 rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-72 z-10">
                      <div className="overflow-y-auto no-scrollbar flex-1">
                        <button
                          onClick={() => {
                            setFilterEvent('');
                            setIsEventDropdownOpen(false);
                            setDeliveriesPage(1);
                          }}
                          className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 flex items-center gap-2 group ${!filterEvent ? 'bg-primary/5' : ''}`}
                        >
                          <span className={`text-[11px] font-black uppercase tracking-widest ${!filterEvent ? 'text-primary' : 'text-base-content/30 group-hover:text-primary'}`}>All Events</span>
                          {!filterEvent && <Check size={12} className="text-primary shrink-0" />}
                        </button>

                        {WEBHOOK_EVENTS.map((ev) => (
                          <button
                            key={ev.id}
                            onClick={() => {
                              setFilterEvent(ev.id);
                              setIsEventDropdownOpen(false);
                              setDeliveriesPage(1);
                            }}
                            className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 last:border-none group flex items-start justify-between gap-2 ${filterEvent === ev.id ? 'bg-primary/5' : ''}`}
                          >
                            <span className={`text-xs font-black truncate ${filterEvent === ev.id ? 'text-primary' : 'text-base-content'}`}>{ev.id}</span>
                            {filterEvent === ev.id && <Check size={12} className="text-primary mt-0.5 shrink-0" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" ref={statusRef}>
                <button
                  onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                  className="flex items-center justify-between gap-2 rounded-xl px-3 py-2 bg-base-200/50 border border-base-content/10 text-xs font-semibold focus:border-primary outline-none w-32 appearance-none cursor-pointer group"
                >
                  <span className="truncate flex-1 text-left">
                    {filterStatus ? (filterStatus === 'success' ? 'Success' : 'Failed') : 'All Status'}
                  </span>
                  <ChevronDown size={14} className={`text-base-content/30 group-hover:text-primary transition-transform ${isStatusDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isStatusDropdownOpen && (
                  <div className="absolute right-0 sm:left-auto sm:right-0 top-full mt-2 min-w-full w-max max-w-xs z-[110] animate-fade-in">
                    <div className="absolute -top-1.5 right-6 sm:right-6 w-3 h-3 bg-base-100 border-l border-t border-base-content/10 rotate-45 z-0" />
                    <div className="relative bg-base-100 border border-base-content/10 rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-72 z-10">
                      <div className="overflow-y-auto no-scrollbar flex-1">
                        <button
                          onClick={() => {
                            setFilterStatus('');
                            setIsStatusDropdownOpen(false);
                            setDeliveriesPage(1);
                          }}
                          className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 flex items-center gap-2 group ${!filterStatus ? 'bg-primary/5' : ''}`}
                        >
                          <span className={`text-[11px] font-black uppercase tracking-widest ${!filterStatus ? 'text-primary' : 'text-base-content/30 group-hover:text-primary'}`}>All Status</span>
                          {!filterStatus && <Check size={12} className="text-primary shrink-0" />}
                        </button>

                        {['success', 'failed'].map((st) => (
                          <button
                            key={st}
                            onClick={() => {
                              setFilterStatus(st);
                              setIsStatusDropdownOpen(false);
                              setDeliveriesPage(1);
                            }}
                            className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all border-b border-base-content/5 last:border-none group flex items-start justify-between gap-2 ${filterStatus === st ? 'bg-primary/5' : ''}`}
                          >
                            <span className={`text-xs font-black truncate capitalize ${filterStatus === st ? 'text-primary' : 'text-base-content'}`}>{st === 'success' ? 'Success' : 'Failed'}</span>
                            {filterStatus === st && <Check size={12} className="text-primary mt-0.5 shrink-0" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={fetchDeliveries}
                className="p-2 rounded-xl bg-base-200 text-base-content/40 hover:text-primary hover:bg-primary/10 transition-all flex-shrink-0"
              >
                <RefreshCw size={16} className={loadingDeliveries ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          <div className="relative">
            {loadingDeliveries && (
              <div className="absolute inset-0 bg-base-100/50 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-xl">
                <RefreshCw className="animate-spin text-primary" size={24} />
              </div>
            )}
            <div className="overflow-x-auto -mx-5">
              <table className="w-full text-left border-collapse min-w-[560px]">
                <thead>
                  <tr className="!bg-base-content/5">
                    <th className="px-4 py-4 text-[14px] font-black text-base-content/30 ">Assignment</th>
                    <th className="px-4 py-4 text-[14px] font-black text-base-content/30 ">URL</th>
                    <th className="px-4 py-4 text-[14px] font-black text-base-content/30 ">Event</th>
                    <th className="px-4 py-4 text-[14px] font-black text-base-content/30 ">Status</th>
                    <th className="pl-8 pr-4 py-4 text-[14px] font-black text-base-content/30 ">Date</th>
                    <th className="pl-4 pr-8 py-4 text-[14px] font-black text-base-content/30  text-right">Inspect</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-base-content/5">
                  {deliveries.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-8 py-16 text-center">
                        <p className="text-[14px] font-black text-base-content/20 ">No deliveries recorded yet</p>
                      </td>
                    </tr>
                  ) : (
                    deliveries.map((d) => (
                      <tr key={d.id} className="hover:bg-primary/[0.02] transition-colors">
                        <td className="px-4 py-4">
                          {d.submitterName ? (
                            <div>
                              <p className="text-[14px] font-black text-base-content truncate max-w-[150px]">{d.submitterName}</p>
                              {d.systemReferenceNumber && <p className="text-[10px] font-black text-primary truncate mt-0.5">{d.systemReferenceNumber}</p>}
                            </div>
                          ) : (
                            <span className="text-[14px] font-bold text-base-content/20 ">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <p className=" font-mono text-sm text-base-content/100 truncate max-w-[300px]" title={d.requestUrl}>
                            {d.requestUrl}
                          </p>
                        </td>
                        <td className="px-4 py-4 text-sm text-base-content/100">{d.eventType}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${d.status === 'success' ? 'bg-success/10 text-success border-success/10' : 'bg-error/10 text-error border-error/10'}`}>
                            {d.status === 'success' ? 'Succeeded' : 'Failed'}
                          </span>
                        </td>
                        <td className="pl-8 pr-4 py-4 text-sm font-mono text-base-content/100">
                          {formatDateTime(d.firedAt)}
                        </td>
                        <td className="pl-4 pr-8 py-4 text-right">
                          <button
                            onClick={async () => {
                              const res = await apiClient.get(`/webhook-deliveries/${d.id}`);
                              setSelectedDelivery(res.data);
                            }}
                            className="px-4 py-1.5 rounded-lg bg-primary/5 text-primary text-xs font-black hover:bg-primary hover:text-primary-content transition-all"
                          >
                            <Eye size={14} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            currentPage={deliveriesPage}
            totalPages={totalPages}
            onPageChange={setDeliveriesPage}
            loading={loadingDeliveries}
          />
        </div>
      )}

      {/* Modals */}
      {(showForm || editWebhook) && (
        <WebhookFormModal
          webhook={editWebhook}
          onClose={() => { setShowForm(false); setEditWebhook(null); }}
          onSaved={handleSaved}
        />
      )}
      {deleteWebhook && (
        <DeleteModal webhook={deleteWebhook} onClose={() => setDeleteWebhook(null)} onDeleted={handleDeleted} />
      )}
      {selectedDelivery && (
        <DeliveryModal delivery={selectedDelivery} onClose={() => setSelectedDelivery(null)} />
      )}
    </div>
  );
};

export default Webhooks;
